class BISForm extends HTMLElement {
  constructor() {
    super();
    this.variantId = null;
    this.companyId = null;
    this.unsubscribe = null;
  }

  connectedCallback() {
    this.variantId = this.getAttribute('variant-id');
    this.companyId = this.getAttribute('company-id');

    if (!this.companyId) {
      console.error('BIS Form: company-id attribute is required');
      return;
    }

    this.init();
  }

  disconnectedCallback() {
    if (this.unsubscribe) {
      this.unsubscribe();
    }
  }

  init() {
    this.form = this.querySelector('.bis-form');
    this.emailInput = this.querySelector('#bis-email-input');
    this.submitBtn = this.querySelector('.bis-submit-btn');
    this.messageBoxSuccess = this.querySelector('.bis-message-box--success');
    this.messageBoxWarning = this.querySelector('.bis-message-box--oos');
    this.similarBtns = this.querySelector('.bis-see-similar-buttons');
    this.loadingSpinner = this.querySelector('.bis-form__loading-state');
    this.productForm = document.querySelector('.product product-info product-form');
    this.relatedProducts = document.querySelector('x-product-recommendations');
    this.errorSpans = this.querySelectorAll('.bis-form__errors > span');

    if (this.form) {
      this.form.addEventListener('submit', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.handleSubmit(e);
      });
    }

    if (this.relatedProducts && this.similarBtns) {
      this.similarBtns.querySelector('button').addEventListener('click', () => {
        const headerHeight = document.querySelector('header').offsetHeight || 0;
        const elementPosition = this.relatedProducts.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - headerHeight;

        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      });
    }

    this.subscribeToVariantChanges();

  }

  subscribeToVariantChanges() {
    this.unsubscribe = window.subscribe(
      PUB_SUB_EVENTS.variantChange,
      this.handleVariantChange.bind(this)
    );
  }

  handleVariantChange(event) {
    if (event?.data?.variant) {
      const variant = event.data.variant;
      this.variantId = variant.id;
      this.updateVisibility(variant.available);
    }
  }

  async handleSubmit(e) {
    e.preventDefault();
    const currentVariantId = this.getVariantId();

    if (!currentVariantId) {
      this.showError('variant');
      return;
    }

    this.setLoading(true);

    const email = this.emailInput.value;
    
    try {
      const success = await this.subscribeToBackInStock(email, currentVariantId);

      setTimeout(() => {
        if (success) {
          this.form.classList.add('hidden');
          this.showMessageBox('success');
          this.showError('clear');
        } else {
          console.error('BIS subscription failed');
          this.showError('generic');
        }
      }, 3000);

    } catch (error) {
      console.error('BIS subscription error:', error);
      this.showError('network');

    } finally {
      setTimeout(() => {
        this.setLoading(false);
      },2800);
    }
  }

  async subscribeToBackInStock(email, variantId) {
    const payload = {
      data: {
        type: "back-in-stock-subscription",
        attributes: {
          profile: {
            data: {
              type: "profile",
              attributes: {
                email: email
              }
            }
          },
          channels: ["EMAIL"]
        },
        relationships: {
          variant: {
            data: {
              type: "catalog-variant",
              id: `$shopify:::$default:::${variantId}`
            }
          }
        }
      }
    };

    const requestOptions = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'revision': '2024-06-15'
      },
      body: JSON.stringify(payload)
    };

    try {
      const response = await fetch(
        `https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${this.companyId}`,
        requestOptions
      );

      if (response.ok || response.status === 202 || response.status === 409) {
        return true;
      }

      if (response.status !== 409) {
        const errorData = await response.text();
        console.error('BIS API Error:', errorData);
      }

      return false;
    } catch (error) {
      console.error('BIS API call failed:', error);
      return false;
    }
  }

  showMessageBox(type) {
    if (this.messageBoxWarning) {
      this.messageBoxWarning.classList.add('hidden');
    }
    if (this.messageBoxSuccess) {
      this.messageBoxSuccess.classList.add('hidden');
    }
    if (this.similarBtns && this.relatedProducts) {
      this.similarBtns.classList.add('hidden');
      this.similarBtns.classList.remove('flex');
    }

    switch(type) {
      case 'warning':
        if (this.messageBoxWarning) {
          this.messageBoxWarning.classList.remove('hidden');
        }
        break;
      case 'success':
        if (this.messageBoxSuccess) {
          this.messageBoxSuccess.classList.remove('hidden');
        }
        if (this.similarBtns && this.relatedProducts) {
          this.similarBtns.classList.remove('hidden');
          this.similarBtns.classList.add('flex');
        }
        break;
      default:
        break;
    }
  }

  setLoading(isLoading) {
    if (this.loadingSpinner) {
      if (isLoading) {
        this.loadingSpinner.classList.remove('hidden');
      } else {
        this.loadingSpinner.classList.add('hidden');
      }
    }
    
    if (this.submitBtn) {
      this.submitBtn.disabled = isLoading;
      
      if (isLoading) {
        this.submitBtn.classList.add('is-loading');
      } else {
        this.submitBtn.classList.remove('is-loading');
      }
    }
    
    if (this.emailInput) {
      this.emailInput.disabled = isLoading;
    }
  }

  showError(type) {
    if(type === 'clear') {
      this.errorSpans.forEach(span => {
        span.classList.add('hidden');
      });

      return null;
    }

    if (this.errorSpans) {
      this.errorSpans.forEach(span => {
        span.classList.add('hidden');
      });

      Array.from(this.errorSpans).filter(el => el.dataset.type === type)[0].classList.remove('hidden');
    }
  }

  getVariantId() {
    if (this.variantId) {
      return this.variantId;
    }

    const urlParams = new URLSearchParams(window.location.search);
    const variantId = urlParams.get('variant');
    if (variantId) {
      return variantId;
    }

    if (window.ShopifyAnalytics && window.ShopifyAnalytics.meta && window.ShopifyAnalytics.meta.selectedVariantId) {
      return window.ShopifyAnalytics.meta.selectedVariantId;
    }

    return null;
  }

  updateVisibility(isAvailable) {
    this.style.display = isAvailable ? 'none' : 'block';

    if(isAvailable) {
      this.productForm.classList.remove('!hidden');
    } else {
      this.productForm.classList.add('!hidden');
    }
  }
}

customElements.define('bis-form', BISForm);
